import { useEffect, useMemo, useRef, useState } from "react"

import { Chip } from "~/ui-rtk/components/ui/common"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { TChipClasses } from "~/ui-rtk/components/ui/common/Chip"

type TInputProps = {
  placeholder: string
}

const COMMIT_KEYS = ["Enter", ",", "Tab"]

type TChipInputProps = {
  value?: string[]
  defaultValue?: string[]
  classes?: Partial<{
    container: string
    input: string
    chip: TChipClasses
  }>
  onChange: (value: string[]) => void
} & Partial<TInputProps>

export const ChipField: React.FC<TChipInputProps> = ({
  value: _value,
  defaultValue,
  onChange,
  classes,
  placeholder,
}) => {
  const isControlled = typeof _value !== "undefined"
  const hasDefaultValue = typeof defaultValue !== "undefined"

  const inputRef = useRef<HTMLInputElement>(null)
  const spanRef = useRef<HTMLSpanElement>(null)
  const [inputWidth, setInputWidth] = useState(30)

  const [preview, setPreview] = useState<string>("")
  const [internalValue, setInternalValue] = useState<string[]>(
    hasDefaultValue ? defaultValue : [],
  )

  const inputValue = isControlled ? _value : internalValue

  const handlePreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    void setPreview(event.target.value)
  }

  const handleChange = (value: string[]) => {
    void onChange(value)

    if (!isControlled) {
      setInternalValue(value)
    }
  }

  // Commit keys are handled on keydown: their defaults (inserting the
  // comma character, Tab moving focus, Enter submitting the form) fire
  // before keyup, so they must be intercepted early.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const isUnique = !inputValue.includes(preview)
    const isValidKeyword = preview.length > 0 && isUnique

    if (event.key === ",") {
      // A comma is only ever a separator, never part of a keyword
      event.preventDefault()
    }

    if (!COMMIT_KEYS.includes(event.key) || !isValidKeyword) {
      return
    }

    event.preventDefault()
    void handleChange([...inputValue, preview])
    void setPreview("")
  }

  const handleKeyUp = (event: React.KeyboardEvent) => {
    event.stopPropagation()
  }

  const handleRemove = (idx: number) => {
    if (idx > inputValue.length - 1) {
      return
    }

    const mutableArray = [...inputValue]
    mutableArray.splice(idx, 1)

    void handleChange(mutableArray)
  }

  const handleFocusInput = () => {
    inputRef.current?.focus()
  }

  const previewInputPlaceholder = useMemo(() => {
    if (inputValue.length) {
      return ""
    }

    return placeholder || "Search"
  }, [inputValue, placeholder])

  const previewInputStyles = useMemo(() => {
    /* eslint-disable no-nested-ternary */
    const width = preview
      ? `${inputWidth}px`
      : !inputValue.length && placeholder
        ? "300px"
        : "70px"

    return {
      width,
    }
    /* eslint-enable no-nested-ternary */
  }, [inputValue, inputWidth, preview, placeholder])

  useEffect(() => {
    if (spanRef.current) {
      // Update input width based on span's width
      setInputWidth(spanRef.current.offsetWidth + 10) // Adding a little padding for caret
    }
  }, [preview])

  return (
    <div
      className={cn(
        "flex flex-wrap items-center w-full gap-1.5 px-4 py-3 bg-basic-blue rounded-lg cursor-text",
        classes?.container,
      )}
      onClick={handleFocusInput}
    >
      {inputValue
        ? (Array.isArray(inputValue) ? inputValue : [inputValue]).map(
            (value, idx) => (
              <Chip
                key={`chip-${idx}`}
                classes={{
                  ...classes?.chip,
                  container: cn(
                    "bg-basic-dark-blue text-white",
                    classes?.chip?.container,
                  ),
                }}
                onRemove={() => handleRemove(idx)}
              >
                <span className="leading-4 text-3.5">{value}</span>
              </Chip>
            ),
          )
        : null}
      <div>
        <input
          ref={inputRef}
          className={cn(
            "px-0 py-1.5 rounded-lg leading-4 grow text-3.5 autofill:[14px] focus:ring-0 bg-transparent text-basic-white placeholder:text-basic-grey-inactive",
            classes?.input,
          )}
          value={preview}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={handlePreview}
          style={previewInputStyles}
          placeholder={previewInputPlaceholder}
        />
        <span
          ref={spanRef}
          aria-hidden="true"
          className="whitespace-pre absolute invisible leading-4 text-3.5"
        >
          {preview || " "}
        </span>
      </div>
      {preview ? (
        <span className="leading-4 text-xs text-basic-grey-inactive whitespace-nowrap select-none">
          press Enter to add
        </span>
      ) : null}
    </div>
  )
}
